import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", headcount: "1-10", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    // Build a mailto payload so the message reaches contact@harguonservices.com without a backend form endpoint.
    const subject = `New enquiry from ${form.name} · ${form.company}`;
    const bodyLines = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Headcount: ${form.headcount}`,
      "",
      "Message:",
      form.message,
    ];
    const mailto = `mailto:contact@harguonservices.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF7EF] text-neutral-950">
      <SiteNav />

      {/* Hero */}
      <section className="border-b border-[#E8DFC5] bg-grid relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(900px 500px at 90% 0%, rgba(201,169,97,0.18), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
            Contact · reach us
          </div>
          <h1 className="font-serif font-black text-[52px] leading-[0.98] md:text-[80px] tracking-[-0.03em] max-w-4xl" data-testid="contact-title">
            Let's set up<br />
            <span className="italic text-gold-gradient">a private call.</span>
          </h1>
          <p className="mt-8 text-lg text-neutral-700 max-w-2xl leading-relaxed">
            Tell us about your team and we'll come back with a written proposal within 48 hours.
            No sales-desk queues, no auto-responders — a partner reads every enquiry.
          </p>
        </div>
      </section>

      {/* Contact grid */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10">
          {/* Left: quick contact tiles */}
          <div className="lg:col-span-5 space-y-4">
            <a href="tel:+919355653653" data-testid="tile-phone" className="group flex items-start justify-between gap-4 border border-[#E8DFC5] bg-white p-6 hover:border-[#C9A961] hover:shadow-[0_0_0_1px_rgba(201,169,97,0.35)] transition-all">
              <div>
                <Phone className="w-5 h-5 mb-3 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Direct line</div>
                <div className="mt-1 font-serif font-black text-3xl tracking-tight">+91 93556 53653</div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-neutral-400 group-hover:text-[#8C6F1E] group-hover:rotate-45 transition-all" />
            </a>
            <a href="mailto:contact@harguonservices.com" data-testid="tile-email" className="group flex items-start justify-between gap-4 border border-[#E8DFC5] bg-white p-6 hover:border-[#C9A961] hover:shadow-[0_0_0_1px_rgba(201,169,97,0.35)] transition-all">
              <div>
                <Mail className="w-5 h-5 mb-3 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Email</div>
                <div className="mt-1 font-mono text-lg break-all">contact@harguonservices.com</div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-neutral-400 group-hover:text-[#8C6F1E] group-hover:rotate-45 transition-all" />
            </a>
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-[#E8DFC5] bg-white p-6">
                <MapPin className="w-5 h-5 mb-3 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Office</div>
                <div className="mt-1 font-serif font-bold text-lg tracking-tight">Haryana, India</div>
                <div className="text-xs font-mono text-neutral-500 mt-1">GSTIN 06ATUPP9372R1Z9</div>
              </div>
              <div className="border border-[#E8DFC5] bg-white p-6">
                <Clock className="w-5 h-5 mb-3 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Hours</div>
                <div className="mt-1 font-serif font-bold text-lg tracking-tight">Mon – Sat</div>
                <div className="text-xs font-mono text-neutral-500 mt-1">10:00 – 19:00 IST</div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="border border-[#C9A961] bg-white p-10 flex flex-col items-start gap-4" data-testid="contact-success">
                <CheckCircle2 className="w-10 h-10 text-[#8C6F1E]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Sent</div>
                  <h3 className="font-serif font-black text-3xl tracking-[-0.02em] mt-1">We'll write back within 48 hours.</h3>
                  <p className="mt-3 text-neutral-600 max-w-md leading-relaxed">
                    Your email client just opened with the message pre-filled. If it didn't, drop us a note directly at
                    <a href="mailto:contact@harguonservices.com" className="text-[#8C6F1E] underline ml-1">contact@harguonservices.com</a>.
                  </p>
                </div>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", company: "", email: "", phone: "", headcount: "1-10", message: "" }); }}
                  className="mt-4 px-5 py-2.5 border border-[#C9A961] font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-[#E8DFC5] bg-white p-8 space-y-5" data-testid="contact-form">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E]">§ Enquiry</div>
                <h3 className="font-serif font-black text-3xl tracking-[-0.02em]">Tell us about your team.</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Your name *</label>
                    <input required value={form.name} onChange={set("name")} data-testid="contact-name"
                      className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Company *</label>
                    <input required value={form.company} onChange={set("company")} data-testid="contact-company"
                      className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Work email *</label>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-form-email"
                      className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Phone</label>
                    <input value={form.phone} onChange={set("phone")} data-testid="contact-phone-input"
                      className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Team size</label>
                  <select value={form.headcount} onChange={set("headcount")} data-testid="contact-headcount"
                    className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                    <option>1-10</option>
                    <option>11-50</option>
                    <option>51-200</option>
                    <option>201-500</option>
                    <option>500+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">What do you need help with?</label>
                  <textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message"
                    placeholder="e.g. We're switching from an in-house sheet to a compliant payroll partner…"
                    className="w-full px-3 py-3 border border-[#E8DFC5] focus:border-[#C9A961] outline-none font-mono text-sm" />
                </div>

                <button type="submit" data-testid="contact-submit"
                  className="w-full flex items-center justify-between px-5 py-3.5 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
                  Send enquiry <ArrowUpRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] font-mono text-neutral-500 leading-relaxed">
                  We reply within 48 hours. Your email client will open with the message pre-filled — this is a static site and we don't
                  store form data anywhere.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
